/**
 * Interactive SDK playground — iter 40.
 *
 * Loads /sdk/v1/cyannect.js dynamically, initializes it against a demo
 * merchant id, and lets the reader inspect the cy_ref cookie + fire the
 * standard events. Zero real merchant traffic — the demo merchant is a
 * throwaway store id we recognize on the ingest endpoint.
 */
import { useEffect, useRef, useState } from "react";
import { Play, RefreshCcw, Cookie, Zap } from "lucide-react";

const DEMO_MERCHANT_ID = "cyannect-demo";

function loadSdk() {
  return new Promise((resolve, reject) => {
    if (window.Cyannect) return resolve(window.Cyannect);
    const s = document.createElement("script");
    s.src = "/sdk/v1/cyannect.js";
    s.async = true;
    s.onload = () => resolve(window.Cyannect);
    s.onerror = () => reject(new Error("Failed to load cyannect.js"));
    document.head.appendChild(s);
  });
}

function readCyRefCookie() {
  const m = document.cookie.match(/(?:^|; )cy_ref=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export default function SdkPlayground() {
  const [ready, setReady] = useState(false);
  const [ref, setRef] = useState(readCyRefCookie());
  const [log, setLog] = useState([]);
  const logRef = useRef(log);
  logRef.current = log;

  const append = (line) => setLog((prev) => [...prev, `${new Date().toLocaleTimeString()}  ${line}`]);

  useEffect(() => {
    loadSdk().then((C) => {
      C.init({ merchantId: DEMO_MERCHANT_ID, debug: true });
      setReady(true);
      setRef(readCyRefCookie());
      append("SDK loaded · Cyannect.init({merchantId: 'cyannect-demo'})");
    }).catch((e) => append("ERROR " + e.message));
  }, []);

  const fire = (event) => {
    if (!window.Cyannect) return;
    window.Cyannect.track(event, { sku: "DEMO-SKU-1", cartValue: 42.00 })
      .then(() => append(`→ ${event} fired · cy_ref=${readCyRefCookie() || "(none)"}`))
      .catch((e) => append(`✗ ${event} failed: ${e.message || e}`));
  };

  const injectDemoRef = () => {
    // Set the cookie manually so readers can see the persistence path even if
    // they didn't come from a `/r/CY-…` link.
    const demo = "CY-DEMO0000";
    document.cookie = `cy_ref=${demo}; path=/; max-age=${30 * 86400}; SameSite=Lax`;
    setRef(demo);
    append("Cookie seeded: cy_ref=CY-DEMO0000 (this bypasses the /r/ redirect for the demo)");
  };

  const clear = () => {
    document.cookie = "cy_ref=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    setRef(null);
    setLog([]);
    append("Cookie cleared · log reset");
  };

  return (
    <section className="pf-card p-6" data-testid="sdk-playground">
      <div className="flex items-center gap-3">
        <Zap className="w-5 h-5 text-cyan-600 shrink-0" />
        <div className="font-display text-lg text-slate-900">SDK playground</div>
        {ready && <span className="pf-chip pf-chip-mint text-[10px]">SDK loaded</span>}
      </div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">
        Fire real <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">cyannect.js</code> events against our demo merchant and watch the cookie + network call happen live. Nothing here counts against any real merchant.
      </p>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white/70 p-4" data-testid="sdk-playground-state">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5">
            <Cookie className="w-3 h-3" /> Current first-party cookie
          </div>
          <div className="font-mono text-sm text-slate-900 mt-2">
            cy_ref = <span className="text-cyan-700">{ref || "(not set)"}</span>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <button onClick={injectDemoRef}
                    className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold"
                    data-testid="sdk-inject-ref">Seed demo cy_ref</button>
            <button onClick={clear}
                    className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                    data-testid="sdk-clear">
              <RefreshCcw className="w-3 h-3" /> Reset
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white/70 p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fire an event</div>
          <div className="flex gap-2 mt-3 flex-wrap">
            {["PRODUCT_VIEWED", "ADD_TO_CART", "CHECKOUT_STARTED", "PURCHASE_INTENT"].map((e) => (
              <button key={e} onClick={() => fire(e)} disabled={!ready}
                      className="pf-btn-primary px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`sdk-fire-${e}`}>
                <Play className="w-3 h-3" /> {e}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-slate-900 text-slate-100 text-xs font-mono p-4 max-h-56 overflow-y-auto"
           data-testid="sdk-playground-log">
        {log.length === 0 ? <span className="text-slate-500">Waiting for events…</span> : log.map((line, i) => (
          <div key={i} className="whitespace-pre-wrap">{line}</div>
        ))}
      </div>
    </section>
  );
}
